// 매장 탭(시안 renderKb 'store') — 현황 띠, CSV 등록, 목록·검색·쪽, 직접 등록 매장만 선택 삭제.
// 스토라넷 동기화 버튼은 계약 §9 대로 두지 않는다(스토라넷은 한 건씩 조회만 된다). 현황 띠 total 은 우리 매장 표의 수.
import { useEffect, useRef, useState } from "react";
import { listStores, storeStats, uploadCsv, bulkDeleteStores, errorText } from "./api";
import { showToast } from "../../../utill/utill";
import { bizFmt, phoneFmt, kstFull } from "../../inquiry/view";
import { ArmButton, DropZone, nf } from "./parts";
import StoreModal from "./StoreModal";

const PER = 10;

function SyncBand({ st }) {
    if (!st) return <div className="sync"></div>;
    const pct = st.total ? ((st.with_mobile / st.total) * 100).toFixed(1) : "0.0";
    return (
        <div className="sync">
            <div><div className="k">등록 매장</div><div className="big">{nf(st.total)}</div></div>
            <div><div className="k">대표자 휴대폰 보유</div><div className="v">{nf(st.with_mobile)} <span style={{ color: "var(--tx3)", fontWeight: 400 }}>({pct}%)</span></div></div>
            <div><div className="k">휴대폰 없음</div><div className="v" style={{ color: "#92400e" }}>{nf(st.without_mobile)}곳 <span style={{ color: "var(--tx3)", fontWeight: 400 }}>임시 비밀번호 대상</span></div></div>
            <div><div className="k">마지막 조회</div><div className="v">{st.last_synced_at ? kstFull(st.last_synced_at) : "-"}</div></div>
            <span className="sp"></span>
            <span className="warn"><i className="fa-solid fa-circle-info"></i> 휴대폰 뒤 4자리가 사장님 앱 초기 비밀번호입니다</span>
        </div>
    );
}

function Pager({ page, pages, onPage }) {
    const lo = Math.max(1, Math.min(page - 2, pages - 4));
    const hi = Math.min(pages, lo + 4);
    const nums = [];
    for (let i = lo; i <= hi; i += 1) nums.push(i);
    return (
        <span className="pg">
            <button disabled={page <= 1} onClick={() => onPage(page - 1)}><i className="fa-solid fa-chevron-left"></i> 이전</button>
            {nums.map((n) => <button key={n} className={n === page ? "on" : ""} onClick={() => onPage(n)}>{n}</button>)}
            <button disabled={page >= pages} onClick={() => onPage(page + 1)}>다음 <i className="fa-solid fa-chevron-right"></i></button>
        </span>
    );
}

export default function Stores({ onChanged }) {
    const [input, setInput] = useState("");
    const [query, setQuery] = useState({ q: "", page: 1, noMobile: false });
    const [tick, setTick] = useState(0);
    const [res, setRes] = useState(null);
    const [st, setSt] = useState(null);
    const [sel, setSel] = useState(() => new Set());
    const [csvBusy, setCsvBusy] = useState(false);
    const [adding, setAdding] = useState(false);
    const seq = useRef(0);

    useEffect(() => { storeStats().then(setSt).catch(() => setSt(null)); }, [tick]);
    useEffect(() => {
        const my = ++seq.current;
        listStores({ q: query.q, page: query.page, size: PER, noMobile: query.noMobile })
            .then((r) => { if (my === seq.current) setRes(r); })
            .catch((e) => { if (my === seq.current) { setRes((r) => r || { total: 0, items: [] }); showToast(errorText(e), "error"); } });
    }, [query, tick]);

    const reload = () => { setTick((n) => n + 1); onChanged && onChanged(); };
    const search = () => {
        const k = input.trim();
        setSel(new Set());
        setQuery((o) => ({ ...o, q: /^[\d-]+$/.test(k) ? k.replace(/-/g, "") : k, page: 1 }));
    };

    async function importCsv(files) {
        const f = files[0];
        if (!f) return;
        setCsvBusy(true);
        try {
            const r = await uploadCsv(f);
            showToast(`${f.name}에서 매장 ${r.total}곳을 등록했습니다. (새로 ${r.created} · 갱신 ${r.updated} · 건너뜀 ${r.skipped})`, "success");
            reload();
        } catch (e) { showToast(errorText(e), "error"); }
        setCsvBusy(false);
    }

    async function removeSel() {
        const ids = [...sel];
        try {
            const r = await bulkDeleteStores(ids);
            setSel(new Set());
            showToast(`직접 등록한 매장 ${r?.deleted ?? ids.length}곳을 삭제했습니다.`, "success");
            reload();
        } catch (e) { showToast(errorText(e), "error"); }
    }

    const items = res?.items || [];
    const total = res?.total || 0;
    const pages = Math.max(1, Math.ceil(total / PER));
    const manualRows = items.filter((s) => s.source === "manual");
    const allOn = manualRows.length > 0 && manualRows.every((s) => sel.has(s.id));
    const toggle = (id, on) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
    const toggleAll = (on) => setSel((s) => { const n = new Set(s); manualRows.forEach((r) => (on ? n.add(r.id) : n.delete(r.id))); return n; });
    const from = (query.page - 1) * PER;

    return (
        <div className="pn">
            <SyncBand st={st} />
            <DropZone style={{ padding: 20 }} accept=".csv,text/csv" busy={csvBusy} onFiles={importCsv}>
                {(pick) => (<>
                    <div className="ic" style={{ fontSize: 26 }}><i className="fa-solid fa-file-csv" style={{ color: "#15803d" }}></i></div>
                    <b>{csvBusy ? "파일을 처리하고 있습니다..." : "CSV 파일을 드래그하거나 클릭해서 업로드"}</b>
                    스토라넷에 없는 매장을 한 번에 등록할 때만 씁니다 · 칸: 상호명, 사업자번호, 대표자명, 전화번호(대표자 휴대폰), 매장연락처, 주소<br />
                    <button className="btn btn-sm" type="button" onClick={(e) => { e.stopPropagation(); pick(); }}><i className="fa-solid fa-file-arrow-up"></i>파일 선택</button>
                </>)}
            </DropZone>
            <div className="pn-h" style={{ padding: "0 0 12px", border: 0, flexWrap: "wrap" }}>
                <h3>매장 목록</h3>
                <span className="tag tag-res">{nf(total)}개 매장</span>
                <button className="btn btn-pri btn-sm" onClick={() => setAdding(true)}><i className="fa-solid fa-plus"></i>매장 추가</button>
                <ArmButton className="btn btn-sm btn-danger" disabled={!sel.size} style={sel.size ? undefined : { opacity: 0.45 }}
                    armedLabel={`정말 ${sel.size}곳 삭제`} onConfirm={removeSel}>
                    <i className="fa-regular fa-trash-can"></i>선택삭제{sel.size ? ` (${sel.size})` : ""}
                </ArmButton>
                <span className="sp"></span>
                <input className="inp" placeholder="매장 검색 (상호·사업자번호·대표자)" value={input} style={{ width: 230 }}
                    onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} />
                <button className="btn btn-pri btn-sm" onClick={search}><i className="fa-solid fa-magnifying-glass"></i>검색</button>
                <button className={`btn btn-sm${query.noMobile ? " btn-pri" : ""}`} onClick={() => setQuery((o) => ({ ...o, noMobile: !o.noMobile, page: 1 }))}>휴대폰 없음만</button>
            </div>
            <div className="tbl-wrap" style={{ maxHeight: "none", minHeight: 0 }}>
                <table className="tbl">
                    <thead><tr>
                        <th style={{ width: 30 }}><input type="checkbox" title="이 쪽 전체 선택" checked={allOn} disabled={!manualRows.length} onChange={(e) => toggleAll(e.target.checked)} /></th>
                        <th>상호명</th><th>사업자번호</th><th>대표자명</th><th>대표자 휴대폰</th><th>매장 연락처</th><th>주소</th><th>앱 가입</th>
                    </tr></thead>
                    <tbody>
                        {items.map((s) => {
                            const manual = s.source === "manual";
                            return (
                                <tr key={s.id}>
                                    <td><input type="checkbox" checked={sel.has(s.id)} disabled={!manual}
                                        title={manual ? undefined : "스토라넷 매장은 스토라넷에서 관리합니다"}
                                        onChange={(e) => toggle(s.id, e.target.checked)} /></td>
                                    <td><b>{s.business_name}</b>{manual && <> <span className="tag tag-res">직접 등록</span></>}</td>
                                    <td className="c-dt">{s.business_number ? bizFmt(s.business_number) : "-"}</td>
                                    <td>{s.owner_name || "-"}</td>
                                    <td>{s.has_mobile && s.phone ? phoneFmt(s.phone) : <span className="tag tag-esc">없음 · 임시 비밀번호</span>}</td>
                                    <td className="c-dt">{s.store_phone ? phoneFmt(s.store_phone) : "-"}</td>
                                    <td style={{ color: "var(--tx2)" }}>{s.address || "-"}</td>
                                    <td>{s.app_joined ? <span className="tag tag-ok">가입</span> : <span className="tag tag-gen">미가입</span>}</td>
                                </tr>
                            );
                        })}
                        {res && !items.length && <tr><td colSpan={8}><div className="empty">데이터가 없습니다</div></td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="pager">
                <span>{total ? `${from + 1} - ${Math.min(from + PER, total)}` : "0"} / 전체 {nf(total)}건</span>
                <Pager page={query.page} pages={pages} onPage={(p) => setQuery((o) => ({ ...o, page: p }))} />
            </div>
            <div className="note">스토라넷 매장은 여기서 고치거나 지우지 않고 스토라넷에서 고칩니다. 선택·삭제는 직접 등록한 매장만 됩니다.</div>
            {adding && <StoreModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); setQuery((o) => ({ ...o, page: 1 })); reload(); }} />}
        </div>
    );
}
